import React, { useState } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { QrCode, Home, Users, MessageSquare, TrendingUp, Settings, LogOut, Menu, X, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import Logo from '@/components/Logo';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Déconnexion réussie');
      navigate('/login');
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const navItems = [
    { path: '/dashboard', icon: <Home className="w-5 h-5" />, label: 'Tableau de bord' },
    { path: '/dashboard/games', icon: <QrCode className="w-5 h-5" />, label: 'Jeu & Fidélisation' },
    { path: '/dashboard/customers', icon: <Users className="w-5 h-5" />, label: 'Clients' },
    { path: '/dashboard/reviews', icon: <MessageSquare className="w-5 h-5" />, label: 'Avis' },
    { path: '/dashboard/analytics', icon: <TrendingUp className="w-5 h-5" />, label: 'Analytiques' },
    { path: '/checkout/basic', icon: <ShoppingBag className="w-5 h-5" />, label: 'Boutique' },
    { path: '/dashboard/settings', icon: <Settings className="w-5 h-5" />, label: 'Paramètres' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <Logo />
              <button 
                onClick={() => setSidebarOpen(false)} 
                className="lg:hidden"
                data-testid="close-sidebar-button"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gradient-to-r from-[#2E59D7] to-[#0082FB] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-gray-200">
            {userProfile && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg" data-testid="user-info">
                <p className="font-semibold text-gray-900 truncate">{userProfile.business_name}</p>
                <p className="text-sm text-gray-600 truncate">{userProfile.email}</p>
              </div>
            )}
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Se déconnecter
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)} 
            className="lg:hidden"
            data-testid="open-sidebar-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1 lg:block hidden">
            <h1 className="text-2xl font-bold text-gray-900">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
    </div>
  );
};

export default DashboardLayout;